import React, { useCallback, useEffect, useRef } from 'react';

import { BaseInput } from 'src/components/BaseInput';
import { EOrientationParam } from '../Orientation';
import { OrientationAdvancedController } from '../OrientationAdvancedController';
import { TOrientationAdvancedProps } from './OrientationAdvanced.types';
import classNames from 'classnames';
import classnames from 'classnames';
import s from './OrientationAdvanced.module.scss';
import { selectSCMFlow } from 'src/redux/scm/scm.selectors';
import { useSelector } from 'react-redux';

export const OrientationAdvanced: React.FC<TOrientationAdvancedProps> = ({
  theme,
  className,
  orientationParam,
  setOrientationParam,
}) => {
  const rendererRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<OrientationAdvancedController>();
  const isSCM = useSelector(selectSCMFlow);
  // Listener of azimuth value change
  const handleOrientationParamChange = useCallback(
    ({ name, value }) => {
      const re = new RegExp(/^-?\d{0,3}(?:,\d{3})*(?:\.\d+)?$/);
      if (re.test(String(value))) {
        setOrientationParam({
          ...orientationParam,
          [name]: value,
        });
      }
    },
    [orientationParam, setOrientationParam],
  );

  useEffect(() => {
    if (controllerRef.current) controllerRef.current.update(orientationParam);
  }, [orientationParam]);

  // Initialize 3D container (only once call)
  useEffect(() => {
    if (rendererRef.current) {
      controllerRef.current = new OrientationAdvancedController(
        rendererRef.current,
        orientationParam,
        theme,
        isSCM ? 'big' : 'normal',
      );
    }

    return () => {
      if (controllerRef.current) controllerRef.current.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSCM]);

  return (
    <div
      className={classnames(s.orientationAdvanced, className, {
        [s.light]: theme === 'light',
      })}>
      <div className={s.orientationAdvancedContainer} ref={rendererRef} />
      <div
        className={classnames(s.orientationAdvancedInputContainer, {
          [s.orientationSidebarInputWrapper]: theme === 'light',
        })}>
        <div
          className={classNames(s.orientationAdvancedInputWrapper, {
            [s.scmWrapper]: isSCM,
          })}>
          <BaseInput
            type="text"
            name={EOrientationParam.AZIMUTH}
            value={`${orientationParam.azimuth}`}
            onChange={handleOrientationParamChange}
            label="Azimuth"
            placeholder=""
            theme={isSCM ? 'filled-gray' : theme === 'light' ? 'filled-dark-gray' : 'line-light'}
            className={classNames(s.orientationAdvancedInput, {
              [s.scm]: isSCM,
            })}
          />
          <span>º</span>
        </div>
        <div
          className={classNames(s.orientationAdvancedInputWrapper, {
            [s.scmWrapper]: isSCM,
          })}>
          <BaseInput
            type="text"
            name={EOrientationParam.TILT}
            value={`${orientationParam.tilt}`}
            onChange={handleOrientationParamChange}
            label="Tilt"
            placeholder=""
            theme={isSCM ? 'filled-gray' : theme === 'light' ? 'filled-dark-gray' : 'line-light'}
            className={classNames(s.orientationAdvancedInput, {
              [s.scm]: isSCM,
            })}
          />
          <span>º</span>
        </div>
      </div>
    </div>
  );
};
